import React, { useCallback } from "react";
import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useQueryData, useProfile, useModalConfirmation, useToast } from "hooks";
import { GET_CHALLENGES } from "graphql/queries";
import { CHALLENGE_CATEGORIES } from "constants/index";
import { PAGINATION_TYPE } from "components/Pagination";
import { ADD_CHALLENGE_RESPONSE } from "graphql/mutations";
import { FormattedMessage } from "react-intl";
import { PrimaryLink } from "components/Links";
import { Col } from "components/Containers";
import CODE_CHALLENGE_STATUS from "../../constants/codeChallengeStatus";

/**
 * useCandidateProfileInterview
 *
 * @param {String}   profileId
 * @param {String}   applicantId
 * @param {Array}    challengeIds
 * @param {Boolean}  isAdmin
 * @param {Number}   resultsPerPage
 */
export default function useCandidateCodeChallenge({
  profileId,
  applicantId,
  challengeIds,
  isAdmin,
  resultsPerPage = 2,
  skip = false,
}) {
  const { toast } = useToast();
  const { show } = useModalConfirmation();
  const [filteredData, setFilteredData] = useState([]);
  const [skills, setSkills] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const { data: profileData, loading: profileLoading } = useProfile({ profileId });
  const [getChallengeUrl, { data: challengeData, loading: loadingChallengeUrl }] = useMutation(ADD_CHALLENGE_RESPONSE);
  const { hasNextPage, handleNextPage, loading, data, refetch } = useQueryData({
    queryName: GET_CHALLENGES,
    keyName: "challenges",
    resultsPerPage,
    paginationType: PAGINATION_TYPE.append,
    variables: {
      profileId,
      filters: challengeIds?.length
        ? { ids: challengeIds }
        : {
            skills: skills,
            disciplines: disciplines,
            types: CHALLENGE_CATEGORIES.vetting,
          },
    },
    skip,
  });

  const unfilteredData = data?.challenges?.nodes;

  useEffect(() => {
    setSkills(profileData?.profile?.skills?.map((item) => item.name));
    setDisciplines(profileData?.profile?.disciplines?.map((item) => item.name));
  }, [profileData?.profile]);

  useEffect(() => {
    if (!challengeIds?.length) {
      refetch({ skills, disciplines });
    }
  }, [skills, disciplines, challengeIds]);

  const handleCodeChallengeClick = useCallback(
    async (challengeId, challengeResponse) => {
      const publicUrl = challengeData?.addChallengeResponse?.challengeResponse?.testUrl;
      const selectedChallenge = data?.challenges?.nodes.find((item) => item.id === challengeId);

      if (isAdmin) {
        // eslint-disable-next-line no-undef
        window.open(challengeResponse?.reportUrl, "_blank");
      } else if (challengeResponse?.publicUrl) {
        window.open(challengeResponse?.publicUrl, "_blank");
      } else {
        if (publicUrl) {
          window.open(publicUrl, "_blank");
        } else {
          await getChallengeUrl({
            variables: {
              input: {
                profileId,
                applicantId,
                params: {
                  challengeId,
                },
              },
            },
            onError: () => toast.error(<FormattedMessage id="useCandidateCodeChallenge.Error" />),
          }).then(async (d) => {
            {
              d?.data?.addChallengeResponse?.challengeResponse?.testUrl &&
                (await show(
                  <FormattedMessage id="useCandidateCodeChallenge.Title" />,
                  <Col gap="1rem">
                    <b>
                      <FormattedMessage
                        id="useCandidateCodeChallenge.Body1"
                        values={{
                          role: selectedChallenge?.name,
                        }}
                      />
                    </b>
                    <ul>
                      <li>
                        <FormattedMessage id="useCandidateCodeChallenge.Body2" />
                      </li>
                      <li>
                        <FormattedMessage id="useCandidateCodeChallenge.Body3" />
                      </li>
                      <li>
                        <FormattedMessage id="useCandidateCodeChallenge.Body4" />
                      </li>
                      <li>
                        <FormattedMessage id="useCandidateCodeChallenge.Body5" />
                      </li>
                    </ul>
                    <FormattedMessage
                      id="useCandidateCodeChallenge.Body6"
                      values={{
                        link: (
                          <PrimaryLink
                            to={d?.data?.addChallengeResponse?.challengeResponse?.testUrl}
                            target="_blank"
                            withMoreIcon
                          >
                            <FormattedMessage id="useCandidateCodeChallenge.TestUrl" />
                          </PrimaryLink>
                        ),
                      }}
                    />
                  </Col>,
                  {
                    secondaryButtonMessageId: "Global.Close",
                    showSave: false,
                  }
                ));
            }
          });
        }
      }
    },
    [challengeData, profileId, isAdmin, show, getChallengeUrl, data]
  );

  // For Admins, only show data that has a response
  useEffect(() => {
    setFilteredData(
      isAdmin
        ? unfilteredData?.filter(
            (challenge) =>
              challenge?.challengeResponse &&
              (challenge?.challengeResponse?.status === CODE_CHALLENGE_STATUS.passed ||
                challenge?.challengeResponse?.status === CODE_CHALLENGE_STATUS.failed)
          )
        : unfilteredData
    );
  }, [unfilteredData, isAdmin]);

  return {
    hasNextPage,
    handleNextPage,
    refetch,
    loading: profileLoading || loading,
    loadingChallengeUrl,
    data: filteredData,
    handleCodeChallengeClick,
  };
}
